import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardList, CheckCircle2 } from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { useStore } from '../hooks/useStore';
import type { ProfileAttributeValue } from '../models';
import { isValidSmsPhone, recordSmsConsent, smsDisclosure } from '../services/communicationConsentService';
import { upsertRegistrationProfile } from '../services/directoryService';
import { getTournamentBySlug, normalizeSeasonSettings } from '../services/tournamentService';
import { createRegistration, setRegistrationStatus, signWaiver } from '../services/registrationService';

export function RegistrationPage() {
  const { slug } = useParams<{ slug: string }>();
  const db = useStore((d) => d);
  const tournament = useMemo(() => (slug ? getTournamentBySlug(slug) : undefined), [slug, db.tournaments]);

  const [step, setStep] = useState<'form' | 'waiver' | 'payment' | 'done'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [eventTextConsent, setEventTextConsent] = useState(false);
  const [marketingTextConsent, setMarketingTextConsent] = useState(false);
  const [school, setSchool] = useState('');
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});
  const [signature, setSignature] = useState('');
  const [registrationId, setRegistrationId] = useState<string>('');
  const [profileId, setProfileId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  if (!tournament || tournament.status === 'archived') {
    return (
      <>
        <ModuleHero eyebrow="Register" title="Event not found" description="" icon={ClipboardList} accent="arc" />
        <div className="empty">No event matches that link.</div>
      </>
    );
  }

  const registration = tournament.settings.season?.registration
    ? normalizeSeasonSettings(tournament.settings.season).registration
    : undefined;
  const registrationEnabled = registration ? registration.enabled : tournament.format !== 'season';
  const isPublished = tournament.status === 'scheduled' || tournament.status === 'in_progress' || tournament.status === 'paused';
  const feeCents = registration?.feeCents ?? 5000;
  const requireWaiver = registration?.requireWaiver ?? true;
  const requiredFields = new Set(registration?.requiredFields ?? ['name', 'email']);
  const customQuestions = registration?.customQuestions ?? [];
  const senderName = db.league.communicationSettings.senderName || db.league.name || 'The Scorekeeper';
  const operationalDisclosure = smsDisclosure('operational', senderName);
  const marketingDisclosure = smsDisclosure('marketing', senderName);

  if (!registrationEnabled || !isPublished) {
    return (
      <>
        <ModuleHero
          eyebrow="Register"
          title={`${tournament.name} registration`}
          description="Registration is not open for this event yet."
          icon={ClipboardList}
          accent="arc"
        />
        <div className="empty">Check back after the league opens registration.</div>
      </>
    );
  }

  const finishWithoutPayment = (id: string) => {
    setRegistrationStatus(id, registration?.autoApprove ? 'approved' : 'pending_review');
    setStep('done');
  };

  const onStart = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const phoneEntered = phone.trim().length > 0;
    const phoneValid = !phoneEntered || isValidSmsPhone(phone);
    const consentNeedsPhone = eventTextConsent || marketingTextConsent;
    if (!name.trim() || !email.trim() || (requiredFields.has('school') && !school.trim()) || (requiredFields.has('phone') && !phoneEntered)) {
      setError('Please complete your name, email, and any required fields.');
      return;
    }
    if (!phoneValid || (consentNeedsPhone && !isValidSmsPhone(phone))) {
      setError(consentNeedsPhone ? 'Enter a valid phone number before selecting text message consent.' : 'Enter a valid phone number.');
      return;
    }
    const missingQuestion = customQuestions.find((question) => question.required && !customAnswers[question.id]?.trim());
    if (missingQuestion) {
      setError(`Please answer: ${missingQuestion.label}`);
      return;
    }
    const attributes: Record<string, ProfileAttributeValue> = {
      ...(school.trim() ? { school: school.trim() } : {}),
      ...Object.fromEntries(
        Object.entries(customAnswers)
          .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
          .map(([key, value]) => [key, value]),
      ),
    };
    const profile = upsertRegistrationProfile({
      displayName: name,
      email,
      phone,
      attributes,
    });
    setProfileId(profile.id);
    const reg = createRegistration({
      tournamentId: tournament.id,
      type: registration?.intakeType === 'team' ? 'team' : 'individual',
      profileId: profile.id,
      formData: {
        name,
        email: email.trim().toLowerCase(),
        phone,
        school,
        eventTextConsent,
        marketingTextConsent,
        ...customAnswers,
      },
    });
    setRegistrationId(reg.id);
    if (eventTextConsent) {
      recordSmsConsent({
        profileId: profile.id,
        registrationId: reg.id,
        tournamentId: tournament.id,
        phone,
        purpose: 'operational',
        senderName,
      });
    }
    if (marketingTextConsent) {
      recordSmsConsent({
        profileId: profile.id,
        registrationId: reg.id,
        tournamentId: tournament.id,
        phone,
        purpose: 'marketing',
        senderName,
      });
    }
    if (requireWaiver) {
      setStep('waiver');
    } else if (feeCents > 0) {
      setRegistrationStatus(reg.id, 'pending_payment');
      setStep('payment');
    } else {
      finishWithoutPayment(reg.id);
    }
  };

  const onSignWaiver = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!signature.trim()) {
      setError('Please type your full legal name as your signature.');
      return;
    }
    signWaiver({
      documentId: registration?.waiverDocumentId ?? `waiver-doc-${tournament.id}`,
      signerName: name,
      signerEmail: email,
      profileId,
      signatureText: signature,
    });
    if (feeCents > 0) {
      setRegistrationStatus(registrationId, 'pending_payment');
      setStep('payment');
    } else {
      finishWithoutPayment(registrationId);
    }
  };

  const onPay = async () => {
    setError(null);
    setPaymentLoading(true);
    try {
      const response = await fetch('/api/stripe/registration/checkout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournamentId: tournament.id,
          tournamentName: tournament.name,
          amountCents: feeCents,
          email,
          playerName: name,
          registrationId,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!response.ok || !data.url) {
        throw new Error(registrationCheckoutError(data.error));
      }
      window.location.assign(data.url);
    } catch (err) {
      setPaymentLoading(false);
      setError(err instanceof Error ? err.message : 'Payment checkout could not start. Please try again.');
    }
  };

  return (
    <>
      <ModuleHero
        eyebrow="Register"
        title={`Sign up for ${tournament.name}`}
        description="A few details, a quick waiver when required, then your spot is held."
        icon={ClipboardList}
        accent="arc"
      />
      <div className="card" style={{ padding: 16, maxWidth: 640 }}>
        {error && <div style={{ color: '#f87171', marginBottom: 12 }}>{error}</div>}

        {step === 'form' && (
          <form onSubmit={onStart} style={{ display: 'grid', gap: 12 }}>
            <label>
              Full name *
              <input value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label>
              Email *
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label>
              Phone
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required={requiredFields.has('phone')}
              />
            </label>
            <label className="directory-consent-row">
              <input
                type="checkbox"
                checked={eventTextConsent}
                onChange={(event) => setEventTextConsent(event.target.checked)}
              />
              <span>
                <strong>Event text messages</strong>
                <span>{operationalDisclosure.text}</span>
              </span>
            </label>
            <label className="directory-consent-row">
              <input
                type="checkbox"
                checked={marketingTextConsent}
                onChange={(event) => setMarketingTextConsent(event.target.checked)}
              />
              <span>
                <strong>Marketing text messages</strong>
                <span>{marketingDisclosure.text}</span>
              </span>
            </label>
            <label>
              School or program{requiredFields.has('school') ? ' *' : ''}
              <input value={school} onChange={(e) => setSchool(e.target.value)} required={requiredFields.has('school')} />
            </label>
            {customQuestions.map((question) => (
              <label key={question.id}>
                {question.label}{question.required ? ' *' : ''}
                {question.type === 'textarea' ? (
                  <textarea
                    className="textarea"
                    value={customAnswers[question.id] ?? ''}
                    onChange={(event) => setCustomAnswers((answers) => ({ ...answers, [question.id]: event.target.value }))}
                    required={question.required}
                  />
                ) : question.type === 'select' ? (
                  <select
                    className="select"
                    value={customAnswers[question.id] ?? ''}
                    onChange={(event) => setCustomAnswers((answers) => ({ ...answers, [question.id]: event.target.value }))}
                    required={question.required}
                  >
                    <option value="">Choose...</option>
                    {(question.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                ) : question.type === 'checkbox' ? (
                  <label className="row" style={{ gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={customAnswers[question.id] === 'yes'}
                      onChange={(event) => setCustomAnswers((answers) => ({ ...answers, [question.id]: event.target.checked ? 'yes' : '' }))}
                      required={question.required}
                    />
                    <span>I agree</span>
                  </label>
                ) : (
                  <input
                    value={customAnswers[question.id] ?? ''}
                    onChange={(event) => setCustomAnswers((answers) => ({ ...answers, [question.id]: event.target.value }))}
                    required={question.required}
                  />
                )}
              </label>
            ))}
            <button className="btn btn--primary" type="submit">Continue</button>
          </form>
        )}

        {step === 'waiver' && (
          <form onSubmit={onSignWaiver} style={{ display: 'grid', gap: 12 }}>
            <h3 style={{ margin: 0 }}>Liability waiver</h3>
            <p style={{ opacity: 0.85, fontSize: 14 }}>
              I acknowledge the inherent risks of roller hockey, release the league and venue from liability, and confirm I have permission to participate.
            </p>
            <label>
              Type your full legal name to sign
              <input value={signature} onChange={(e) => setSignature(e.target.value)} required />
            </label>
            <button className="btn btn--primary" type="submit">Sign and continue</button>
          </form>
        )}

        {step === 'payment' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <h3 style={{ margin: 0 }}>Payment</h3>
            <p style={{ opacity: 0.85, fontSize: 14 }}>
              Registration fee: <strong>{formatMoney(feeCents)}</strong>. Payment opens through Stripe checkout.
            </p>
            <button className="btn btn--primary" type="button" onClick={onPay} disabled={paymentLoading}>
              {paymentLoading ? 'Opening Stripe...' : `Pay ${formatMoney(feeCents)}`}
            </button>
          </div>
        )}

        {step === 'done' && (
          <div style={{ display: 'grid', gap: 12, textAlign: 'center' }}>
            <CheckCircle2 size={48} style={{ margin: '0 auto', color: '#34d399' }} />
            <h3 style={{ margin: 0 }}>You're in!</h3>
            <p style={{ opacity: 0.85 }}>
              We received your registration. The league will review and confirm your roster spot shortly.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function registrationCheckoutError(error?: string): string {
  if (error === 'connect_not_configured' || error === 'connect_not_ready') {
    return 'Registration payments are not ready yet. Contact the league before paying.';
  }
  if (error === 'missing_fields' || error === 'invalid_email') {
    return 'Please review your registration details before paying.';
  }
  return 'Payment checkout could not start. Please try again.';
}
